// SAD-455: TanStack Query bindings for source-observation conflicts. The RPCs
// live on zou's ProjectDataService (ListObservationConflicts,
// ResolveObservationConflict, ReverseObservationResolution); the acting user is
// the authenticated principal server-side, never sent from here.
import { Code, ConnectError } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { projectDataClient } from '../../lib/connect/clients';
const CONFLICTS_KEY = 'observation-conflicts';
export function isPermissionDenied(err) {
    return err instanceof ConnectError && err.code === Code.PermissionDenied;
}
/** Conflict sets for one entity, resolved ones included so history stays visible. */ export function useObservationConflicts(nodeId, enabled = true) {
    return useQuery({
        queryKey: [
            CONFLICTS_KEY,
            nodeId
        ],
        enabled,
        queryFn: async ()=>{
            const resp = await projectDataClient.listObservationConflicts({
                nodeId,
                includeResolved: true
            });
            return resp.conflicts;
        },
        // A denied viewer stays denied; do not hammer the gateway.
        retry: (count, err)=>!isPermissionDenied(err) && count < 2
    });
}
function useConflictMutation(fn, failMsg) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: fn,
        onSuccess: ()=>queryClient.invalidateQueries({
                queryKey: [
                    CONFLICTS_KEY
                ]
            }),
        onError: (err)=>{
            if (isPermissionDenied(err)) {
                toast.error("You don't have access to do this. Request it from your admin or team supervisor.");
                return;
            }
            toast.error(err instanceof Error ? err.message : failMsg);
        }
    });
}
export function useResolveObservationConflict() {
    return useConflictMutation((args)=>projectDataClient.resolveObservationConflict({
            ...args,
            chosenObservationId: args.chosenObservationId ?? '',
            correctedValue: args.correctedValue ?? ''
        }), 'Failed to resolve conflict');
}
export function useReverseObservationResolution() {
    return useConflictMutation((args)=>projectDataClient.reverseObservationResolution(args), 'Failed to reverse resolution');
}
